import { isDevMode } from '@angular/core';
import * as _ from 'lodash';
import { AthleteMetrics } from './athlete-metrics';
import { Conference, Sport, Teamlevel } from './conference';
import { Game } from './game';
import { LeaguePool } from './leaguepool';
import { Country, State } from './locations';
import { Organization } from './organization';
import { Team } from './team'
import { ThirdParty } from './third_parties';

export class User {
  _id: string;
  username: string;
  firstname: string;
  lastname: string;
  phonenumber?: string;
  twitter?: string;
  password?: string;
  lastLogin?: Date;
  userRoles: UserRole[];
  currentUserRole: UserRole;
  forceLogoutDate?: Date;
	qcAnalyst?: boolean; //for statisticians
	ballerTvAnalyst?: boolean; //for statisticians
	taskRequestDate?: Date; //for statisticians
  isShowNotification?: boolean;
  oldids?: number[];
  oldshooterid?: number;

  //not stored in db
  team?: Team;
  metrics?: AthleteMetrics;
  userRoleDisplay?: string;
  relevantUserRole?: UserRole;
  title?: string;
  leaguePool?: LeaguePool;
  jerseyNumber?: number;

	//stat dashboard stuff
  rank?: number
	status?: 'Online' | 'Offline' | 'Idle';
	idleTime?: Date;
	breakdownsAssigned?: number;
	breakdownsFinished?: number;
	singleBreakdowns?: number;
	singleAvgTime?: string;
	fullBreakdowns?: number;
	fullAvgTime?: string;
	breakdownAccuracy?: string;
	currentBreakdownGame?: Game; //for statisticians
	thirdParty?: ThirdParty;
  emailInfo?: EmailInfo;
}

export interface UserRole {
  role: UserRoleValue;
  teamid?: Team['_id'];
  seasons?: number[];
  title?: string;
  leaguepoolid?: LeaguePool['_id'];
  jerseyNumber?: number;
  jerseyNumberArray?: {seasonid: string, jerseyNumbers: number[]}[]; // seasonid is "default" for all seasons
	subscriptionExpires?: Date;
  singleUser?: boolean;
	thirdpartyid?: ThirdParty['_id'];
	teamname?: string;
  confirmSeason?: number;

	//scouting
	sportid?: Sport;
	positions?: string[];
	assignmentOrder?: {[position: string]: number};
  
  //not in db
  team?: Team;
  leaguePool?: LeaguePool;
	thirdParty?: ThirdParty;
}

export type UserRoleValue = 'admin' | 'subadmin' | 'junioradmin' | 'boardmember' | 'teamadmin' | 'coach' | 'athlete' | 'single' | 'statsapi' | 'statmanager' | 'shooter' | 'fan' | 'demo' | 'leagueadmin' | 'recruitingcoach' | 'parent' | 'thirdparty';

export interface SavedCard {
  _id?: string;
  userid: User['_id'];
  lastfour: string;
  customerid: string;
  expirationdate: Date;
  cardtype?: string;
  token: string;
  default?: boolean;
  updatedAt?: Date;
  createdAt?: Date;
}

export const coachFootballRoles: string[] = [
  'Head Coach', 
  'Offensive Coordinator', 
  'Defensive Coordinator',
  'Athletic Director',
  'Special Teams Coordinator', 
	'Recruiting Coordinator',
  'Quarterbacks Coach',
  'Running Backs Coach',
  'Wide Receivers Coach',
  'Tight Ends Coach',
  'Offensive Line Coach',
  'Defensive Line Coach',
  'Linebackers Coach',
  'Defensive Backs Coach'
];

export interface ClipView {
  userid: User['_id'];
  teamid?: Team['_id'];
  gameid: Game['_id'];
  clipnumber: number;
  start: Date;
  end?: Date;
}

export interface WatchTimeData {
  [userid: string]: {
    timewatched: number, 
    timeWatchedGame: number,
    timeWatchedScout: number,
    timeWatchedPractice: number,
    avgTimeToPlay: number,
    clips: number,
    clipsGame: number,
    clipsScout: number,
    clipsPractice: number,
  }
} 

export interface ClipViewData {
  gameid: string;
  clipnumber: number;
  timewatched: number;
  game?: Game;
  gameType?: string;
  matchup?: string;
}

export interface Firebase {
  _id?: string;
  userid: User['_id'];
  token?: string;
  updatedAt?: Date;
}

export interface AccountRequest {
  _id?: string;
  email: string;
  firstname: string;
  lastname: string;
  userrole?: UserRoleValue;
  orgtype?: string;
  sportid: Sport;
  teamname?: string;
  division?: string;
  requestdate: Date;
  approved?: boolean;
  denied?: boolean;
  countryid?: Country['_id'];
  stateid?: State['_id'];
  gender?: string;
  invitecode?: string;
  extrainfo?: string;
  phonenumber?: string;

  //not in db
  state?: State;
  country?: Country;
}

export interface UserNotificationSettings {
  _id?: string;
  userid: User['_id'];
  gameUploads: UserNotificationOption;
  playlistShared: UserNotificationOption;
  playbookShared: UserNotificationOption;
  breakdowns: UserNotificationOption;
  cameraUpdates: UserNotificationOption;
}

export class UserNotificationOption {
  email: boolean = true;
  push: boolean = true;
}

export enum NotificationType {
  gameUploads = 'gameUploads',
  playlistShared = 'playlistShared',
  playbookShared = 'playbookShared',
  breakdowns = 'breakdowns',
  cameraUpdates = 'cameraUpdates'
}
export const notificationTypes: NotificationType[] = [NotificationType.gameUploads, NotificationType.playlistShared, NotificationType.breakdowns, NotificationType.playbookShared, NotificationType.cameraUpdates];

export interface UserProfile {
	_id?: string;
	userid: User['_id'];
	profilePicExists?: boolean;
	description?: string;
}

export const getProfilePicSrc = (userid: string) => {
	const dir = (isDevMode()) ? "dev_profile_pictures" : "profile_pictures";
	return `https://d2htis0rx2m2xo.cloudfront.net/${dir}/${userid}`;
}

export interface EmailInfo {
	_id?: string;
	email: string;
	bounces: number;
	complaints: number;
}

export const getJerseyNumberDisplay = (user: User, teamid: string, relevantUserRole?: UserRole) => {
  relevantUserRole ??= _.find(user?.userRoles ?? [], userRole => userRole.teamid === teamid && _.includes(['athlete'], userRole.role));
  if (!relevantUserRole) return null;
  if (relevantUserRole.jerseyNumberArray?.length) {
    let allJerseyNums: number[] = [];
    _.forEach(relevantUserRole.jerseyNumberArray, jnObj => {
      _.forEach(jnObj?.jerseyNumbers ?? [], num => {
        allJerseyNums.push(num);
      });
    });
    allJerseyNums = _.uniq(_.filter(allJerseyNums, num => num != null));
    let jerseyNumStr: string = '';
    _.forEach(allJerseyNums, (num, index) => {
      if (index > 0) jerseyNumStr += ', ';
      jerseyNumStr += num;
    });
    return jerseyNumStr;
  }
  
  return _.toString(relevantUserRole.jerseyNumber) ?? null;
}

export const getValidJerseyNumberForUser = (user: User, teamid: string, seasonid?: number) => {
  const relevantUserRole = _.find(user?.userRoles ?? [], userRole => userRole.teamid === teamid && _.includes(['athlete'], userRole.role) && (!seasonid || !userRole?.seasons?.length || _.includes(userRole.seasons, seasonid)));
  if (!relevantUserRole) return null;
  if (relevantUserRole.jerseyNumberArray?.length) {
    const seasonJnObj = seasonid ? _.find(relevantUserRole.jerseyNumberArray, {seasonid: _.toString(seasonid)}) : null;
    if (seasonJnObj?.jerseyNumbers?.length) return seasonJnObj.jerseyNumbers[0];
    const defaultJerseyNumObj = _.find(relevantUserRole.jerseyNumberArray, {seasonid: 'default'});
    if (defaultJerseyNumObj?.jerseyNumbers?.length) return defaultJerseyNumObj.jerseyNumbers[0];
  }
  
  return relevantUserRole.jerseyNumber ?? null;
}

export const getUserFromJerseyNumber = (users: User[], jerseyNumber: number, teamid: string, seasonid?: number) => {
  return _.find(users, (user: User) => {
    const relevantUserRole = _.find(user?.userRoles ?? [], userRole => userRole.teamid === teamid && _.includes(['athlete'], userRole.role) && (!seasonid || !userRole?.seasons?.length || _.includes(userRole.seasons, seasonid)));
    if (!relevantUserRole) return false;
    if (relevantUserRole.jerseyNumberArray?.length) {
      const seasonJerseyNumObj = seasonid ? _.find(relevantUserRole.jerseyNumberArray, {seasonid: _.toString(seasonid)}) : null;
      if (seasonJerseyNumObj) return _.includes(seasonJerseyNumObj.jerseyNumbers ?? [], jerseyNumber);
      const defaultJerseyNumObj = _.find(relevantUserRole.jerseyNumberArray, {seasonid: 'default'});
      return _.includes(defaultJerseyNumObj?.jerseyNumbers ?? [], jerseyNumber);
    }
    return relevantUserRole.jerseyNumber === jerseyNumber;
  });
}

export const defaultParentPrice: number = 5;
